import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';
import { ChevronDown } from 'lucide-react';

const faqCategories = [
  {
    title: 'The Big Questions',
    questions: [
      {
        q: 'What about exams?',
        a: 'Nova Roots focuses on mastery, not memorisation. Children develop deep understanding that prepares them for any assessment. When examinations are required for transitions (KCPE, international options), we prepare children specifically—but we don\'t let exams dictate our daily learning.'
      },
      {
        q: 'Will my child fall behind?',
        a: 'No. Mastery-based learning means children progress when they truly understand—not when the calendar says so. Most children actually advance faster because learning is personalised, focused and engaging. No gaps. No surface learning.'
      },
      {
        q: 'Is this recognised?',
        a: 'Nova Roots is registered in Kenya and follows a rigorous curriculum that exceeds national standards in core areas. Our graduates will be prepared for Kenyan national examinations, international curricula, or alternative pathways. We partner with accreditation bodies as we scale.'
      },
      {
        q: 'How is progress measured?',
        a: 'Daily. Our AI-powered system tracks exactly what your child masters. You receive real-time updates through a parent portal. Monthly family conferences replace surprise report cards. You always know where your child stands.'
      },
      {
        q: 'What happens after age 10?',
        a: 'Nova Roots will grow with our founding cohort. We are building a continuous learning pathway through age 16. Early families help shape what comes next. Your child won\'t outgrow us.'
      },
    ]
  },
  {
    title: 'The 2+4 Model',
    questions: [
      {
        q: 'What does "2+4" actually mean?',
        a: "2 hours of focused, AI-powered academic mastery in the morning. 4 hours of hands-on, real-world learning in the afternoon. No traditional homework—just daily reading and termly projects. Deep, meaningful education."
      },
      {
        q: 'Only 2 hours of academics? Is that enough?',
        a: "Yes. Traditional schools spend 6+ hours on academics but much of that is waiting, administration, or one-size-fits-all instruction. Our AI system ensures every minute is optimised for your child's specific needs. Focused beats long."
      },
      {
        q: 'What are the 4 domains?',
        a: 'Technology (build, code, create), Trade (make, fix, sell), Agriculture (grow, nurture, sustain), Leadership (lead, decide, serve). Children rotate through all four, discovering their strengths and interests.'
      },
      {
        q: 'Is there homework?',
        a: "We do not assign traditional homework. Children spend a full, focused day engaged in deep learning, practical work and collaboration. After school, their time should be spent playing, resting, exploring their interests and being with family. This balance is essential for healthy development.\n\nHowever, daily reading is strongly encouraged. Reading builds language, imagination, focus and independent thinking. Even 10–20 minutes a day has a measurable impact on vocabulary, comprehension and long-term academic confidence.\n\nInstead of homework, children engage in termly learning projects shaped by their interests. These may involve building, researching, creating or solving real-world problems—developing ownership, creativity and critical thinking far more effectively than worksheets."
      },
    ]
  },
  {
    title: 'Admissions & Logistics',
    questions: [
      {
        q: 'When does Nova Roots open?',
        a: 'September 2026. Summer Camp (July 2026) is your first chance to experience the model.'
      },
      {
        q: 'Where are you located?',
        a: 'Runda / Gigiri Corridor, Nairobi, Kenya. A purpose-designed learning village.'
      },
      {
        q: 'What age range?',
        a: '4-10 years for the founding cohort. We grow with our learners.'
      },
      {
        q: 'How many children?',
        a: '32 learners maximum. Intentionally small. Every child known deeply.'
      },
      {
        q: 'What is the application process?',
        a: '1) Expression of interest. 2) Parent information session. 3) Family interview. 4) Child visit day. 5) Enrollment offer. No entrance exams. We select for alignment, not performance.'
      },
    ]
  },
  {
    title: 'Philosophy & Approach',
    questions: [
      {
        q: 'What is a "Walezi"?',
        a: 'Swahili for nurturers. Our educators are guides and mentors—not lecturers. They know each child deeply and support individual journeys. Human connection, amplified by technology.'
      },
      {
        q: 'What about socialisation?',
        a: '32 children in mixed-age pods. Deep friendships. Collaborative projects. Shared meals. Community service. No child is invisible or left out.'
      },
      {
        q: 'How do you handle different learning needs?',
        a: 'The model is inherently inclusive. AI adapts to different paces. Small groups allow individual attention. Variety of activities means every child finds areas to shine. We welcome neurodivergent children.'
      },
    ]
  },
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-[#EBE8E0] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-start justify-between text-left"
      >
        <span className="text-sm font-medium text-[#1A3C34] pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-[#C27856] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="pb-5">
          <p className="text-sm font-light text-[#5C6B6B] leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                Information
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                Frequently Asked Questions
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mt-8 text-lg font-light leading-relaxed text-[#2D3A3A]">
                Everything you need to know about Nova Roots, the 2+4 model, admissions and what to expect.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      {faqCategories.map((category, catIndex) => (
        <section 
          key={category.title}
          className={`py-20 md:py-28 ${catIndex % 2 === 0 ? 'bg-[#F2F0E9]' : 'bg-[#F9F8F4]'}`}
        >
          <div className="container-nova">
            <div className="max-w-3xl mx-auto">
              <ScrollReveal>
                <h2 className="font-serif text-2xl md:text-3xl font-normal text-[#1A3C34] mb-10">
                  {category.title}
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <div className="bg-white rounded-2xl border border-[#EBE8E0] px-6">
                  {category.questions.map((item, index) => (
                    <FAQItem key={index} question={item.q} answer={item.a} />
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              Still have questions?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/80 max-w-md mx-auto">
              Submit an enquiry and we'll be in touch.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <Link 
              to="/enquire" 
              className="mt-10 inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="faq-enquire-btn"
            >
              Enquire
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
